import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import Fade from '@mui/material/Fade/Fade';
import Paper from '@mui/material/Paper/Paper';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import React from 'react';

// Google Analytics' own opt-out switch; it has to name the measurement id in public/index.html
const GA_DISABLE = 'ga-disable-G-SQW659K9TG';

export const CookieBanner = () => {
    const [showBanner, setShowBanner] = React.useState(false);
    const bannerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const cookie = localStorage.getItem('cookie');
        if (!cookie) {
            setShowBanner(true);
        }
    }, []);

    // while the banner shows, keep scrolled-to content (links, form buttons) above it instead of underneath it
    React.useEffect(() => {
        const root = document.documentElement;
        if (!showBanner || !bannerRef.current) {
            root.style.scrollPaddingBottom = '';
            root.style.paddingBottom = '';
            return;
        }
        const update = () => {
            const height = `${(bannerRef.current?.offsetHeight || 0) + 16}px`;
            root.style.scrollPaddingBottom = height;
            root.style.paddingBottom = height;
        };
        update();
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('resize', update);
            root.style.scrollPaddingBottom = '';
            root.style.paddingBottom = '';
        };
    }, [showBanner]);

    const acceptCookies = () => {
        localStorage.setItem('cookie', 'accepted');
        // @ts-ignore
        window[GA_DISABLE] = false;

        setShowBanner(false);
    }

    const closeBanner = () => {
        localStorage.setItem('cookie', 'rejected');
        // @ts-ignore
        window[GA_DISABLE] = true;

        setShowBanner(false);
    }

    return (
        <Fade appear={false} in={showBanner}>
            <Paper
                ref={bannerRef}
                role="dialog"
                aria-modal="false"
                aria-label="Cookie banner"
                elevation={6}
                tabIndex={-1}
                sx={{
                    // a small corner card rather than a full-width bar, so it covers as little of the page as possible
                    position: 'fixed',
                    bottom: { xs: 8, sm: 16 },
                    left: { xs: 8, sm: 16 },
                    right: { xs: 8, sm: 'auto' },
                    maxWidth: { sm: 360 },
                    m: 0,
                    px: 2,
                    py: { xs: 0.75, sm: 1.5 },
                    zIndex: 1051
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                >
                    <Box sx={{ flexShrink: 1 }}>
                        {/* phones: one short line, so the card is a slim strip rather than a block over the page */}
                        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            We use cookies to improve your experience and analyze traffic.
                        </Typography>
                        <Typography variant="body2" sx={{ display: { xs: 'block', sm: 'none' } }}>
                            Cookies for analytics?
                        </Typography>
                    </Box>
                    <Stack
                        gap={1}
                        direction={{ xs: 'row', sm: 'column' }}
                        sx={{ flexShrink: 0 }}
                    >
                        <Button size="small" onClick={acceptCookies} variant="contained">
                            Allow all
                        </Button>
                        <Button size="small" onClick={closeBanner}>
                            Reject all
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Fade>
    )
}